import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../serices/firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore'; 
import { Loader2, ArrowLeft, ShieldCheck, CheckCircle2, XCircle, Coins } from 'lucide-react';
import SponsorStrip from '../components/SponsorStrip';

const Vote = () => {
  const { id } = useParams();
  const [nominee, setNominee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState(""); // Default to 10 KES
  
  const [transactionId, setTransactionId] = useState(null);
  const [paying, setPaying] = useState(false);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "nominations", id), (doc) => {
      if (doc.exists()) setNominee({ id: doc.id, ...doc.data() });
      setLoading(false);
    });
    return () => unsub();
  }, [id]);

  useEffect(() => {
    if (!transactionId) return;
    const unsub = onSnapshot(doc(db, "mpesa_logs", transactionId), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        if (data.status === "completed") {
          setStatus("success");
          setPaying(false);
        } else if (data.status === "failed") {
          setStatus("failed");
          setPaying(false);
          setTimeout(() => setStatus("idle"), 4000); 
        }
      }
    });
    return () => unsub();
  }, [transactionId]);

  const handlePayment = async () => {
    let cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.startsWith('0')) cleanPhone = '254' + cleanPhone.substring(1);
    if (cleanPhone.length !== 12) return alert("Enter a valid Safaricom number");
    if (amount < 10) return alert("Minimum amount is 10 KES");

    setPaying(true);
    setStatus("pending");

    try {
      const res = await fetch('/.netlify/functions/stkPush', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phoneNumber: cleanPhone, 
          nomineeId: id, 
          nomineeName: nominee.name,
          amount: Number(amount) // Send dynamic amount
        })
      });
      
      const data = await res.json();
      if (data.CheckoutRequestID) {
        setTransactionId(data.CheckoutRequestID);
      } else {
        throw new Error(data.error || "M-Pesa unavailable.");
      }
    } catch (err) {
      alert(err.message);
      setPaying(false);
      setStatus("idle");
    }
  };

  if (loading) return <div className="h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]" size={40} /></div>;

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6 py-20 font-sans relative">
      <div className="w-full max-w-md bg-zinc-950 border border-white/10 p-10 rounded-[2.5rem] text-center shadow-2xl overflow-hidden relative z-10">
        
        {status === "success" && (
            <div className="absolute inset-0 bg-black/95 z-50 flex flex-col items-center justify-center p-8 animate-in fade-in zoom-in duration-500">
                <CheckCircle2 size={80} className="text-green-500 mb-6" />
                <h2 className="text-3xl font-black uppercase italic text-white tracking-tighter">Votes Cast!</h2>
                <p className="text-white/40 text-[10px] mt-4 uppercase tracking-[0.2em] font-bold">You've successfully cast <span className="text-white text-sm">{Math.floor(amount/10)}</span> votes for <br/> <span className="text-white text-sm">{nominee.name}</span></p>
                <button onClick={() => setStatus("idle")} className="mt-10 bg-[#D4AF37] text-black px-10 py-4 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-white transition-all">Vote Again</button>
            </div>
        )}

        {/* HEADER */}
        <div className="mb-8 p-4 border border-white/5 bg-white/[0.02] rounded-2xl flex items-center gap-4">
          <img src={nominee?.imageUrl} className="w-14 h-14 rounded-full object-cover border border-[#D4AF37]/20" />
          <div className="text-left flex-1">
            <p className="font-black uppercase text-white truncate w-32">{nominee?.name}</p>
            <p className="text-[10px] text-[#D4AF37] font-bold uppercase">{nominee?.voteCount || 0} Votes</p>
          </div>
        </div>

        <div className="animate-in slide-in-from-bottom duration-700">
          <h2 className="text-2xl font-black uppercase mb-8 italic text-white tracking-tighter">Support with Votes</h2>
          
          {/* AMOUNT INPUT */}
          <div className="text-left mb-6">
            <label className="text-[10px] font-bold text-white/40 uppercase tracking-widest block mb-2 pl-1 italic">Amount (KES)</label>
            <div className="relative">
              <input 
                type="number" 
                step="10"
                min="10"
                placeholder="Amount"
                value={amount} 
                onChange={(e) => setAmount(e.target.value)} 
                className="w-full bg-black border border-white/10 rounded-xl py-4 px-6 text-xl font-black text-center text-[#D4AF37] focus:border-[#D4AF37] outline-none transition-all"/>

              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 font-black">KES</div>
            </div>
            <p className="text-center text-[10px] font-black text-white/30 uppercase mt-3 tracking-widest">
                
                This equals{" "}
<span className="text-[#D4AF37]">
  {amount !== "" && Number(amount) >= 10
    ? Math.floor(Number(amount) / 10)
    : 0}
</span>{" "}
Votes
            </p>
          </div>


          {/* PHONE INPUT */}
          <div className="text-left mb-8">
            <label className="text-[10px] font-bold text-white/40 uppercase tracking-widest block mb-2 pl-1 italic">Safaricom Phone Number</label>
            <input 
              type="tel" 
              placeholder="07XX XXX XXX" 
              value={phone} 
              onChange={(e) => setPhone(e.target.value)} 
              className="w-full bg-black border border-white/10 rounded-xl py-4 px-6 text-xl font-black text-center text-white focus:border-[#D4AF37] outline-none" 
            />
          </div>

          <button 
              disabled={paying || !phone}
              onClick={handlePayment}
              className="w-full bg-[#D4AF37] text-black py-6 rounded-xl font-black uppercase tracking-[0.4em] mb-6 hover:bg-white transition-all disabled:opacity-50 flex items-center justify-center gap-3"
          >
            {paying ? <Loader2 className="animate-spin" /> : `Confirm & Pay KES ${amount}`}
          </button>

          <div className="flex flex-col items-center gap-2 opacity-30">
             <div className="flex items-center gap-2">
                <ShieldCheck size={12} />
                <span className="text-[7px] font-bold uppercase tracking-widest">Daraja Production Secured</span>
             </div>
             <p className="text-[7px] font-black uppercase tracking-[0.2em]">Technology Partner: INICUS INNOVATIONS</p>
          </div>
        </div>
      </div>
      <SponsorStrip />
    </div>
  );
};

export default Vote;



















/*import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../serices/firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore'; 
import { Loader2, ArrowLeft, ShieldCheck, CheckCircle2, XCircle } from 'lucide-react';
import SponsorStrip from '../components/SponsorStrip';

const Vote = () => {
  const { id } = useParams();
  const [nominee, setNominee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("");
  
  // PAYMENT STATES
  const [transactionId, setTransactionId] = useState(null);
  const [paying, setPaying] = useState(false);
  const [status, setStatus] = useState("idle"); // idle, pending, success, failed

  // 1. Fetch Nominee Details & Listen for Live Vote Updates
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "nominations", id), (doc) => {
      if (doc.exists()) {
        setNominee({ id: doc.id, ...doc.data() });
      }
      setLoading(false);
    });
    return () => unsub();
  }, [id]);

  // 2. REAL-TIME TRANSACTION LISTENER (The "Magic" Link to M-Pesa Callback)
  useEffect(() => {
    if (!transactionId) return;

    const unsub = onSnapshot(doc(db, "mpesa_logs", transactionId), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        if (data.status === "completed") {
          setStatus("success");
          setPaying(false);
        } else if (data.status === "failed") {
          setStatus("failed");
          setPaying(false);
          // Reset button after 4 seconds so they can try again
          setTimeout(() => setStatus("idle"), 4000); 
        }
      }
    });
    return () => unsub();
  }, [transactionId]);

  // 3. HANDLE PAYMENT INITIATION
  const handlePayment = async () => {
    let cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.startsWith('0')) cleanPhone = '254' + cleanPhone.substring(1);
    if (cleanPhone.length !== 12) return alert("Enter a valid Safaricom number (e.g. 0712345678)");

    setPaying(true);
    setStatus("pending");

    try {
      const res = await fetch('/.netlify/functions/stkPush', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phoneNumber: cleanPhone, 
          nomineeId: id, 
          nomineeName: nominee.name 
        })
      });
      
      const data = await res.json();
      
      if (data.CheckoutRequestID) {
        setTransactionId(data.CheckoutRequestID);
        alert("Please check your phone and enter your M-Pesa PIN.");
      } else {
        throw new Error(data.error || "M-Pesa service unavailable. Try again later.");
      }
    } catch (err) {
      alert(err.message);
      setPaying(false);
      setStatus("idle");
    }
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]" size={40} /></div>;

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6 py-20 font-sans relative overflow-hidden">
      
      {/* Background Decorative Glow *//*
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#D4AF37]/5 blur-[120px] rounded-full pointer-events-none" />

      <Link to={`/vote/${nominee?.slug}`} className="mb-8 text-[10px] font-bold text-white/40 uppercase tracking-widest flex items-center gap-2 hover:text-[#D4AF37] relative z-10">
        <ArrowLeft size={14} /> Back to Profile
      </Link>

      <div className="w-full max-w-md bg-zinc-950 border border-white/10 p-10 rounded-[2.5rem] text-center backdrop-blur-md relative z-10 shadow-2xl overflow-hidden">
        
        {/* SUCCESS OVERLAY - Appears instantly when callback is received *//*
        {status === "success" && (
            <div className="absolute inset-0 bg-black/95 z-50 flex flex-col items-center justify-center p-8 animate-in fade-in zoom-in duration-500">
                <CheckCircle2 size={80} className="text-green-500 mb-6" />
                <h2 className="text-3xl font-black uppercase italic tracking-tighter text-white">Vote Recorded!</h2>
                <p className="text-white/40 text-[10px] mt-4 uppercase tracking-[0.2em] leading-relaxed font-bold">Thank you for supporting <br/> <span className="text-white text-sm">{nominee.name}</span></p>
                <button onClick={() => setStatus("idle")} className="mt-10 bg-[#D4AF37] text-black px-10 py-4 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-white transition-all">Vote Again</button>
            </div>
        )}

        {/* NOMINEE SUMMARY HEADER *//*
        <div className="mb-10 p-4 border border-white/5 bg-white/[0.02] rounded-2xl flex items-center gap-4">
          <img src={nominee?.imageUrl} className="w-16 h-16 rounded-full object-cover border border-[#D4AF37]/20" alt={nominee?.name} />
          <div className="text-left flex-1">
            <p className="text-[9px] font-black text-[#D4AF37] tracking-[0.3em] uppercase mb-1 italic">Cast Vote For</p>
            <p className="font-black uppercase text-white truncate w-32">{nominee?.name}</p>
          </div>
          <div className="text-right">
             <p className="text-2xl font-black text-[#D4AF37] leading-none">{nominee?.voteCount || 0}</p>
             <p className="text-[8px] uppercase font-bold text-white/20">Votes</p>
          </div>
        </div>

        {/* ACTIVE VOTING FORM *//*
        <div className="animate-in slide-in-from-bottom duration-700">
          <h2 className="text-3xl font-black uppercase mb-8 italic text-white tracking-tighter">M-Pesa Payment</h2>
          
          <div className="text-left mb-8">
            <label className="text-[10px] font-bold text-white/40 uppercase tracking-widest block mb-2 pl-1 italic">Safaricom Phone Number</label>
            <input 
              type="tel" 
              placeholder="07XX XXX XXX" 
              disabled={paying}
              value={phone} 
              onChange={(e) => setPhone(e.target.value)} 
              className="w-full bg-black border border-white/10 rounded-xl py-5 px-6 text-2xl font-black text-center text-[#D4AF37] tracking-widest focus:border-[#D4AF37] outline-none transition-all disabled:opacity-50" 
            />
          </div>

          <button 
              disabled={paying || !phone}
              onClick={handlePayment}
              className="w-full bg-[#D4AF37] text-black py-6 rounded-xl font-black uppercase tracking-[0.4em] mb-6 hover:bg-white transition-all disabled:opacity-50 shadow-[0_0_30px_rgba(212,175,55,0.2)] flex items-center justify-center gap-3"
          >
            {paying ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                <span>Waiting for PIN...</span>
              </>
            ) : (
              "Cast Vote (10 KES)"
            )}
          </button>

          {status === "failed" && (
              <div className="flex items-center justify-center gap-2 text-red-500 mb-4 animate-bounce">
                  <XCircle size={14} />
                  <span className="text-[9px] font-black uppercase tracking-widest">Transaction Failed</span>
              </div>
          )}

          <div className="flex items-center justify-center gap-2 text-white/20">
              <ShieldCheck size={14} />
              <span className="text-[8px] font-bold uppercase tracking-widest italic">Daraja Production Secured</span>
          </div>
        </div>
        
        <p className="mt-8 text-[9px] text-white/20 uppercase tracking-[0.2em] font-medium leading-relaxed italic border-t border-white/5 pt-6">
          MZINYI TALENT AWARDS 2026 <br /> UNLIMITED VOTES PER NOMINEE.
        </p>
          <p className="text-[7px] font-black uppercase tracking-[0.3em] text-[#D4AF37]/40 mt-4">
        Technology Partner: <span className="text-[#D4AF37]/80">INICUS INNOVATIONS LIMITED</span>
    </p>
      </div>

      <SponsorStrip />
    </div>
  );
};

export default Vote;*/












/*import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../serices/firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore'; // onSnapshot for real-time
import { Loader2, ArrowLeft, ShieldCheck, Timer, CheckCircle2 } from 'lucide-react';
import SponsorStrip from '../components/SponsorStrip';

const Vote = () => {
  const { id } = useParams();
  const [nominee, setNominee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("");
  const [paying, setPaying] = useState(false);
  const [status, setStatus] = useState("idle"); // idle, pending, success

  // Listen for real-time vote updates
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "nominations", id), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setNominee({ id: doc.id, ...data });
        // If the vote count increased while we were waiting, show success
        if (status === "pending") setStatus("success");
      }
      setLoading(false);
    });
    return () => unsub();
  }, [id, status]);

  const handlePayment = async () => {
    // 1. Sanitize Phone
    let cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.startsWith('0')) cleanPhone = '254' + cleanPhone.substring(1);
    if (cleanPhone.length !== 12) return alert("Enter valid number (07... or 01...)");

    setPaying(true);
    setStatus("pending");

    try {
      const response = await fetch('/.netlify/functions/stkPush', {
        method: 'POST',
        body: JSON.stringify({
          phoneNumber: cleanPhone,
          nomineeId: id,
          nomineeName: nominee.name
        })
      });

      if (!response.ok) throw new Error("Request failed");
      alert("Check your phone and enter M-Pesa PIN");
    } catch (err) {
      alert("Error: " + err.message);
      setPaying(false);
      setStatus("idle");
    }
  };

  if (loading) return <div className="h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]"/></div>;

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6 py-20">
      <div className="w-full max-w-md bg-zinc-950 border border-white/10 p-10 rounded-[2.5rem] text-center shadow-2xl relative">
        
        {status === "success" && (
            <div className="absolute inset-0 bg-black/90 z-20 rounded-[2.5rem] flex flex-col items-center justify-center p-6 animate-in fade-in duration-500">
                <CheckCircle2 size={60} className="text-green-500 mb-4" />
                <h2 className="text-2xl font-black uppercase italic">Vote Recorded!</h2>
                <p className="text-white/40 text-sm mt-2">Thank you for supporting {nominee.name}</p>
                <button onClick={() => setStatus("idle")} className="mt-8 text-[#D4AF37] font-bold uppercase text-xs tracking-widest">Vote Again</button>
            </div>
        )}

        {/* NOMINEE SUMMARY *//*
        <div className="mb-10 p-4 border border-white/5 bg-white/[0.02] rounded-2xl flex items-center gap-4">
          <img src={nominee?.imageUrl} className="w-16 h-16 rounded-full object-cover border border-[#D4AF37]/20" />
          <div className="text-left flex-1">
            <p className="text-[9px] font-black text-[#D4AF37] tracking-[0.3em] uppercase">Voting For</p>
            <p className="font-black uppercase text-white truncate w-32">{nominee?.name}</p>
          </div>
          <div className="text-right">
             <p className="text-2xl font-black text-[#D4AF37] leading-none">{nominee?.voteCount || 0}</p>
             <p className="text-[8px] uppercase font-bold text-white/20">Votes</p>
          </div>
        </div>

        <div className="animate-in slide-in-from-bottom duration-700">
            <h2 className="text-3xl font-black uppercase mb-8 italic">M-Pesa Payment</h2>
            <div className="text-left mb-8">
              <label className="text-[10px] font-bold text-white/40 uppercase tracking-widest block mb-2 pl-1 italic">Phone Number</label>
              <input 
                type="tel" 
                placeholder="0712 345 678" 
                value={phone} 
                onChange={(e) => setPhone(e.target.value)} 
                className="w-full bg-black border border-white/10 rounded-xl py-4 px-6 text-2xl font-black text-center text-[#D4AF37] outline-none" 
              />
            </div>
            <button 
                disabled={paying}
                onClick={handlePayment}
                className="w-full bg-[#D4AF37] text-black py-6 rounded-xl font-black uppercase tracking-[0.4em] mb-6 hover:bg-white transition-all disabled:opacity-50"
            >
              {paying ? <Loader2 className="animate-spin mx-auto"/> : "Pay 10 KES"}
            </button>
            <div className="flex items-center justify-center gap-2 text-white/20"><ShieldCheck size={14} /><span className="text-[8px] font-bold uppercase tracking-widest">Daraja API Secured</span></div>
        </div>
        
        <p className="mt-8 text-[9px] text-white/20 uppercase tracking-[0.2em] font-medium italic">
          MZINYI TALENT AWARDS 2026 <br /> ONE VOTE PER TRANSACTION.
        </p>
      </div>
      <SponsorStrip />
    </div>
  );
};

export default Vote;*/


